import type {
    CustomStatisticConfig,
    CustomStatisticField,
    CustomStatisticMethod,
    CustomStatisticRequestField,
    Field,
    FieldCellValue,
    FilterFormType,
    RecordLikeProtocol,
    TypeInstanceMap,
    ViewOptions
} from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'

import { CUSTOM_STATISTIC_METHOD_OPTIONS } from '../constants/customStatistic'
import { recTakeFilterVariable } from './actionsEngine/filterResolver'

export function canSelectAsStatisticKeyField(field: Field): boolean {
    return field.type !== 'notes' && field.innerType !== 'RICH_TEXT'
}

export function getAllowedStatisticMethods(innerType?: TypeInstanceMap): CustomStatisticMethod[] {
    const type = innerType || 'NULL'
    return CUSTOM_STATISTIC_METHOD_OPTIONS.filter(item => item.innerTypeList.includes(type)).map(item => item.value)
}

export function isStatisticMethodAllowed(method: CustomStatisticMethod | undefined, innerType?: TypeInstanceMap): boolean {
    if (!method) {
        return false
    }
    return getAllowedStatisticMethods(innerType).includes(method)
}

export function isCustomStatisticFieldReady(field: CustomStatisticField): field is CustomStatisticField & { method: CustomStatisticMethod } {
    return Boolean(field.id && field.dsId && field.targetFieldId && field.method)
}

export function collectViewFieldIdsFromFilter(filter?: FilterFormType): string[] {
    const vars = recTakeFilterVariable(filter?.expression) || []
    const ids = new Set<string>()
    for (const item of vars) {
        if (item.type === VariableType.VIEW && item.viewVariable?.fieldId) {
            ids.add(item.viewVariable.fieldId)
        }
    }
    return [...ids]
}

export function getReadyCustomStatisticFields(fields?: CustomStatisticField[]): CustomStatisticRequestField[] {
    return (fields ?? []).filter(isCustomStatisticFieldReady).map(field => ({
        id: field.id,
        dsId: field.dsId,
        targetFieldId: field.targetFieldId,
        method: field.method,
        filter: field.filter
    }))
}

export function getViewCustomStatisticConfig(config?: ViewOptions): CustomStatisticConfig | undefined {
    if (config?.viewType !== 'custom') {
        return
    }
    return config.customStatistic
}

export function getCustomStatisticViewFields(config?: CustomStatisticConfig, dsId = ''): Field[] {
    return (config?.fields ?? [])
        .filter(field => field.id)
        .map(field => ({
            id: field.id,
            name: field.name || '统计字段',
            type: 'number',
            dsId,
            innerType: 'NUMBER',
            number: {}
        }))
}

export function findCustomStatisticViewField(
    config?: CustomStatisticConfig,
    fieldId?: string,
    dsId = ''
): Field | undefined {
    if (!fieldId) {
        return
    }
    return getCustomStatisticViewFields(config, dsId).find(field => field.id === fieldId)
}

export function resolveViewVariableField(
    schema: Record<string, Field> | undefined,
    fieldId: string,
    config?: CustomStatisticConfig,
    dsId = ''
): Field | undefined {
    return schema?.[fieldId] ?? findCustomStatisticViewField(config, fieldId, dsId)
}

export const CUSTOM_STATISTIC_FIELD_ID_PREFIX = 'stat_'

export function isCustomStatisticFieldId(fieldId: string): boolean {
    return fieldId.startsWith(CUSTOM_STATISTIC_FIELD_ID_PREFIX)
}

export function applyCustomStatisticToRecord(
    record: RecordLikeProtocol | undefined,
    values?: Record<string, number | null>
): RecordLikeProtocol | undefined {
    if (!record || !values) {
        return record
    }
    const content = { ...record.content }
    for (const [fieldId, value] of Object.entries(values)) {
        content[fieldId] = { value: value ?? undefined }
    }
    return { ...record, content }
}

export function normalizeUniqueIdValue(value: FieldCellValue | unknown): string {
    if (value == null) {
        return ''
    }
    if (Array.isArray(value) || typeof value === 'object') {
        return JSON.stringify(value)
    }
    return String(value)
}
